import React, { createContext, useState, useEffect, useContext } from 'react';

// Factory function to create a context for a specific platform
const createPlatformContext = (platformName, storageKey) => {
  const Context = createContext();

  const Provider = ({ children }) => {
    const [state, setState] = useState(() => {
      if (typeof window !== 'undefined') {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          try {
            const parsed = JSON.parse(saved);
            if (parsed.profiles) return parsed; // Already in new format
            // Migrate from flat format
            return {
              profiles: [{ id: 'default', name: 'Product 1', data: parsed }],
              activeProfileId: 'default'
            };
          } catch (e) {}
        }
      }
      return {
        profiles: [{ id: 'default', name: 'Product 1', data: {} }],
        activeProfileId: 'default'
      };
    });

    useEffect(() => {
      if (typeof window !== 'undefined') {
        localStorage.setItem(storageKey, JSON.stringify(state));
      }
    }, [state]);

    const activeProfile = state.profiles.find(p => p.id === state.activeProfileId) || state.profiles[0];
    const sharedData = activeProfile.data;

    const updateSharedData = (updates) => {
      setState((prev) => {
        const newProfiles = prev.profiles.map(p => 
          p.id === prev.activeProfileId 
            ? { ...p, data: { ...p.data, ...updates } } 
            : p
        );
        return { ...prev, profiles: newProfiles };
      });
    };

    const addProfile = (name) => {
      const id = Date.now().toString();
      setState(prev => ({
        ...prev,
        profiles: [...prev.profiles, { id, name: name || `Product ${prev.profiles.length + 1}`, data: {} }],
        activeProfileId: id
      }));
    };

    const switchProfile = (id) => {
      setState(prev => ({ ...prev, activeProfileId: id }));
    };

    const deleteProfile = (id) => {
      setState(prev => {
        if (prev.profiles.length <= 1) return prev;
        const newProfiles = prev.profiles.filter(p => p.id !== id);
        return {
          ...prev,
          profiles: newProfiles,
          activeProfileId: prev.activeProfileId === id ? newProfiles[0].id : prev.activeProfileId
        };
      });
    };

    return (
      <Context.Provider value={{ 
        sharedData, 
        updateSharedData,
        profiles: state.profiles,
        activeProfileId: state.activeProfileId,
        addProfile,
        switchProfile,
        deleteProfile
      }}>
        {children}
      </Context.Provider>
    );
  };

  return { Context, Provider, useContextHook: () => useContext(Context) };
};

// Create contexts for each platform
export const Amazon = createPlatformContext('Amazon', 'amazonGlobalData');
export const Usa = createPlatformContext('USA', 'usaGlobalData');
export const Flipkart = createPlatformContext('Flipkart', 'flipkartGlobalData');
export const Meesho = createPlatformContext('Meesho', 'meeshoGlobalData');
export const SocialMedia = createPlatformContext('SocialMedia', 'socialMediaGlobalData');
export const Keyword = createPlatformContext('Keyword', 'keywordGlobalData');

// Master Provider to wrap the app
export const GlobalPlatformProvider = ({ children }) => {
  return (
    <Amazon.Provider>
      <Usa.Provider>
        <Flipkart.Provider>
          <Meesho.Provider>
            <SocialMedia.Provider>
              <Keyword.Provider>
                {children}
              </Keyword.Provider>
            </SocialMedia.Provider>
          </Meesho.Provider>
        </Flipkart.Provider>
      </Usa.Provider>
    </Amazon.Provider>
  );
};
